import { useCallback, useEffect, useRef } from 'react'

/**
 * A hook that debounces a function call by the specified delay.
 * Useful for API calls that should only be triggered after user stops performing an action.
 *
 * @param callback - The function to debounce
 * @param delay - The delay in milliseconds
 * @returns An object with the debounced function and a cancel function
 *
 * @example
 * ```tsx
 * const { debouncedCallback: debouncedSave, cancel } = useDebounce((data: FormData) => {
 *   saveToAPI(data)
 * }, 500)
 *
 * // In your component:
 * const handleChange = (newData: FormData) => {
 *   debouncedSave(newData) // Will only call saveToAPI after 500ms of inactivity
 * }
 *
 * const handleCancel = () => {
 *   cancel() // Cancels any pending call
 * }
 * ```
 */
export const useDebounce = <TArgs extends readonly unknown[], TReturn = void>(
  callback: (...args: TArgs) => TReturn,
  delay: number
): {
  debouncedCallback: (...args: TArgs) => void
  cancel: () => void
} => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const debouncedCallback = useCallback(
    (...args: TArgs) => {
      cancel()

      timeoutRef.current = setTimeout(() => {
        callback(...args)
        timeoutRef.current = null
      }, delay)
    },
    [callback, delay, cancel]
  )

  useEffect(() => {
    return () => {
      cancel()
    }
  }, [cancel])

  return { debouncedCallback, cancel }
}
